import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { US_STATES } from '../../data/usLocations';

interface Firm {
  id: number;
  name: string;
  email: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  website: string;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  users: FirmUser[];
}

interface FirmUser {
  id: number;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  phone?: string | null;
  role: string;
}

interface FirmMember {
  id: number;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  phone?: string | null;
  phone_extension?: string | null;
  designation_id?: number | null;
  designation?: { id: number; name: string } | null;
  is_approved: boolean;
  created_at: string;
}

interface Designation {
  id: number;
  name: string;
  is_active: boolean;
}

const EMPTY_FIRM_FORM = {
  name: '',
  poc_first_name: '',
  poc_last_name: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  website: '',
  status: 'approved' as 'pending' | 'approved' | 'rejected',
};

const EMPTY_MEMBER_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  phone_extension: '',
  designation_id: '',
};

const AUTHORIZED_DISCLAIMER =
  'Authorized is defined as the owner of the account and the authorized signatory.';

const normalizeState = (value: string) => {
  const match = US_STATES.find(
    state => state.abbreviation === value || state.name.toLowerCase() === value.toLowerCase()
  );
  return match ? match.abbreviation : value;
};

const AdminFirms: React.FC = () => {
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(true);

  // Approve / Reject modal
  const [selectedFirm, setSelectedFirm] = useState<Firm | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');

  // Create firm modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [firmForm, setFirmForm] = useState({ ...EMPTY_FIRM_FORM });
  const [firmFormSubmitting, setFirmFormSubmitting] = useState(false);
  const [createAcknowledged, setCreateAcknowledged] = useState(false);
  const [editAcknowledged, setEditAcknowledged] = useState(false);

  // Edit firm modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFirm, setEditFirm] = useState<Firm | null>(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_FIRM_FORM });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Paralegal management modal
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [memberFirm, setMemberFirm] = useState<Firm | null>(null);
  const [members, setMembers] = useState<FirmMember[]>([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberFormOpen, setMemberFormOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<FirmMember | null>(null);
  const [memberForm, setMemberForm] = useState({ ...EMPTY_MEMBER_FORM });
  const [memberSubmitting, setMemberSubmitting] = useState(false);
  const [designations, setDesignations] = useState<Designation[]>([]);

  useEffect(() => {
    fetchFirms();
  }, []);

  const fetchFirms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/firms');
      setFirms(response.data.data);
    } catch {
      toast.error('Failed to load firms');
    } finally {
      setLoading(false);
    }
  };

  // ── Create Firm ────────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setFirmForm({ ...EMPTY_FIRM_FORM });
    setCreateAcknowledged(false);
    setCreateModalOpen(true);
  };

  const handleCreateFirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createAcknowledged) {
      toast.error('Confirm the authorized signatory before creating the firm');
      return;
    }
    setFirmFormSubmitting(true);
    try {
      const response = await api.post('/admin/firms', firmForm);
      setFirms(prev => [response.data.data, ...prev]);
      toast.success('Firm created successfully');
      setCreateModalOpen(false);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to create firm';
      toast.error(msg);
    } finally {
      setFirmFormSubmitting(false);
    }
  };

  // ── Edit Firm ──────────────────────────────────────────────────────────────
  const openEditModal = (firm: Firm) => {
    setEditFirm(firm);
    setEditForm({
      name: firm.name ?? '',
      poc_first_name: firm.users?.[0]?.first_name || firm.users?.[0]?.name?.split(' ')[0] || '',
      poc_last_name: firm.users?.[0]?.last_name || firm.users?.[0]?.name?.split(' ').slice(1).join(' ') || '',
      email: firm.users?.[0]?.email || firm.email || '',
      address: firm.address ?? '',
      city: firm.city ?? '',
      state: normalizeState(firm.state ?? ''),
      zip: firm.zip ?? '',
      phone: firm.users?.[0]?.phone || firm.phone || '',
      website: firm.website ?? '',
      status: firm.status,
    });
    setEditAcknowledged(Boolean(
      (firm.users?.[0]?.first_name || firm.users?.[0]?.name) &&
      (firm.users?.[0]?.last_name || (firm.users?.[0]?.name || '').includes(' '))
    ));
    setEditModalOpen(true);
  };

  const handleEditFirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFirm) return;
    if (!editAcknowledged) {
      toast.error('Confirm the authorized signatory before saving');
      return;
    }
    setEditSubmitting(true);
    try {
      const response = await api.put(`/admin/firms/${editFirm.id}`, editForm);
      setFirms(prev =>
        prev.map(f => (f.id === editFirm.id ? response.data.data : f))
      );
      toast.success('Firm updated successfully');
      setEditModalOpen(false);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to update firm';
      toast.error(msg);
    } finally {
      setEditSubmitting(false);
    }
  };

  // ── Paralegal Management ───────────────────────────────────────────────────
  const loadMembers = async (firmId: number) => {
    const res = await api.get(`/admin/firms/${firmId}/members`);
    setMembers(res.data.data);
  };

  const openMemberModal = async (firm: Firm) => {
    setMemberFirm(firm);
    setMemberModalOpen(true);
    setMemberFormOpen(false);
    setEditingMember(null);
    setMemberForm({ ...EMPTY_MEMBER_FORM });
    setMemberLoading(true);
    try {
      const [memberRes, designationRes] = await Promise.all([
        api.get(`/admin/firms/${firm.id}/members`),
        api.get('/admin/masters/designations', { params: { active_only: 1 } }),
      ]);
      setMembers(memberRes.data.data);
      setDesignations(designationRes.data.data);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setMemberLoading(false);
    }
  };

  const handleSaveMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberFirm) return;
    setMemberSubmitting(true);
    try {
      const payload = {
        ...memberForm,
        designation_id: Number(memberForm.designation_id),
      };
      if (editingMember) {
        await api.put(`/admin/firms/${memberFirm.id}/members/${editingMember.id}`, payload);
        toast.success('Member updated');
      } else {
        await api.post(`/admin/firms/${memberFirm.id}/members`, payload);
        toast.success('Member added. A password was emailed to them.');
      }
      setMemberForm({ ...EMPTY_MEMBER_FORM });
      setMemberFormOpen(false);
      setEditingMember(null);
      await loadMembers(memberFirm.id);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (errors) {
        const firstError = Object.values(errors)[0];
        toast.error(Array.isArray(firstError) ? firstError[0] : String(firstError));
      } else {
        toast.error(err.response?.data?.message || 'Failed to save member');
      }
    } finally {
      setMemberSubmitting(false);
    }
  };

  const startEditMember = (member: FirmMember) => {
    setEditingMember(member);
    setMemberForm({
      first_name: member.first_name || member.name.split(' ')[0] || '',
      last_name: member.last_name || member.name.split(' ').slice(1).join(' ') || '',
      email: member.email,
      phone: member.phone || '',
      phone_extension: member.phone_extension || '',
      designation_id: member.designation_id ? String(member.designation_id) : '',
    });
    setMemberFormOpen(true);
  };

  const resetMemberPassword = async (member: FirmMember) => {
    if (!memberFirm) return;
    if (!window.confirm(`Email a new password to ${member.email}?`)) return;
    try {
      await api.post(`/admin/firms/${memberFirm.id}/members/${member.id}/reset-password`);
      toast.success('A new password was emailed');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    }
  };

  // ── Approve / Reject ───────────────────────────────────────────────────────
  const openModal = (firm: Firm, action: 'approve' | 'reject') => {
    setSelectedFirm(firm);
    setActionType(action);
    setRejectionReason('');
    setActionModalOpen(true);
  };

  const closeActionModal = () => {
    setActionModalOpen(false);
    setSelectedFirm(null);
  };

  const handleApprove = async () => {
    if (!selectedFirm) return;
    try {
      const response = await api.post(`/admin/firms/${selectedFirm.id}/approve`);
      setFirms(firms.map(f => (f.id === selectedFirm.id ? response.data.data : f)));
      toast.success('Firm approved successfully');
      closeActionModal();
    } catch {
      toast.error('Failed to approve firm');
    }
  };

  const handleReject = async () => {
    if (!selectedFirm || !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      const response = await api.post(`/admin/firms/${selectedFirm.id}/reject`, {
        rejection_reason: rejectionReason,
      });
      setFirms(firms.map(f => (f.id === selectedFirm.id ? response.data.data : f)));
      toast.success('Firm rejected successfully');
      closeActionModal();
    } catch {
      toast.error('Failed to reject firm');
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className='badge bg-warning'>Pending</span>;
      case 'approved':
        return <span className='badge bg-success'>Approved</span>;
      case 'rejected':
        return <span className='badge bg-danger'>Rejected</span>;
      default:
        return <span className='badge bg-secondary'>Unknown</span>;
    }
  };

  const firmFieldRow = (
    label: string,
    name: keyof typeof EMPTY_FIRM_FORM,
    type: string,
    required: boolean,
    value: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  ) => (
    <div className='mb-3'>
      <label className='form-label'>
        {label} {required && <span className='text-danger'>*</span>}
      </label>
      <input
        type={type}
        className='form-control'
        name={name}
        value={value}
        onChange={onChange}
        required={required}
      />
    </div>
  );

  const stateField = (
    value: string,
    onChange: (value: string) => void
  ) => {
    const options = US_STATES.map(state => ({ value: state.abbreviation, label: state.name }));
    if (value && !options.some(option => option.value === value)) {
      options.unshift({ value, label: value });
    }
    return (
      <div className='mb-3'>
        <label className='form-label'>State</label>
        <select className='form-select' value={value} onChange={e => onChange(e.target.value)}>
          <option value=''>Select state</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
    );
  };

  const authorizedSignatoryFields = (
    form: typeof EMPTY_FIRM_FORM,
    setForm: React.Dispatch<React.SetStateAction<typeof EMPTY_FIRM_FORM>>,
    acknowledged: boolean,
    setAcknowledged: (value: boolean) => void,
    checkboxId: string
  ) => (
    <>
      <div className='col-12'>
        <p className='text-muted small mb-2'>{AUTHORIZED_DISCLAIMER}</p>
        <div className='form-check mb-3'>
          <input
            id={checkboxId}
            className='form-check-input'
            type='checkbox'
            checked={acknowledged}
            onChange={e => setAcknowledged(e.target.checked)}
          />
          <label className='form-check-label' htmlFor={checkboxId}>
            Authorized signatory
          </label>
        </div>
      </div>
      <div className='col-md-6'>
        {firmFieldRow('Authorized Signatory First Name', 'poc_first_name', 'text', true, form.poc_first_name,
          e => setForm(current => ({ ...current, poc_first_name: e.target.value })))}
      </div>
      <div className='col-md-6'>
        {firmFieldRow('Authorized Signatory Last Name', 'poc_last_name', 'text', true, form.poc_last_name,
          e => setForm(current => ({ ...current, poc_last_name: e.target.value })))}
      </div>
      <div className='col-md-6'>
        {firmFieldRow('Authorized Signatory Email', 'email', 'email', true, form.email,
          e => setForm(current => ({ ...current, email: e.target.value })))}
      </div>
    </>
  );

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ height: '50vh' }}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='container-fluid'>
      <div className='page-header d-flex justify-content-between align-items-center'>
        <h1 className='page-title'>Law Firm Management</h1>
        <button className='btn btn-primary' onClick={openCreateModal}>
          <i className='ri-add-line me-1'></i> Create Firm
        </button>
      </div>

      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Registered Firms</h3>
        </div>
        <div className='card-body'>
          {firms.length === 0 ? (
            <div className='text-center py-5'>
              <i className='ri-folder-info-line text-muted' style={{ fontSize: '48px' }}></i>
              <h5 className='mt-3'>No firms registered yet</h5>
              <p className='text-muted'>New firm registrations will appear here for approval</p>
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-hover table-vcenter'>
                <thead>
                  <tr>
                    <th>Firm Name</th>
                    <th>Admin</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {firms.map(firm => (
                    <tr key={firm.id}>
                      <td>
                        <div className='d-flex flex-column'>
                          <span className='fw-semibold mb-1'>{firm.name}</span>
                          {firm.email && (
                            <span className='text-muted small'>
                              <i className='ri-mail-line me-1'></i>{firm.email}
                            </span>
                          )}
                          {firm.website && (
                            <a href={firm.website} target='_blank' rel='noopener noreferrer' className='text-muted small'>
                              {firm.website}
                            </a>
                          )}
                        </div>
                      </td>
                      <td>
                        {firm.users && firm.users.length > 0 ? (
                          <div className='d-flex flex-column'>
                            <span>{firm.users[0].name}</span>
                            <span className='text-muted small'>{firm.users[0].email}</span>
                          </div>
                        ) : (
                          <span className='text-muted'>No admin</span>
                        )}
                      </td>
                      <td>
                        <div className='d-flex flex-column'>
                          {firm.phone && <span className='small'>{firm.phone}</span>}
                          <span className='text-muted small'>
                            {[firm.city, firm.state, firm.zip].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      </td>
                      <td>{getStatusBadge(firm.status)}</td>
                      <td>
                        <span className='text-muted small'>
                          {new Date(firm.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td>
                        <div className='d-flex flex-wrap gap-1'>
                          {/* Edit */}
                          <button
                            className='btn btn-sm btn-outline-secondary'
                            onClick={() => openEditModal(firm)}
                            title='Edit firm details'
                          >
                            <i className='ri-edit-line'></i>
                          </button>

                          {/* Paralegals */}
                          <button
                            className='btn btn-sm btn-outline-info'
                            onClick={() => openMemberModal(firm)}
                            title='Manage members'
                          >
                            <i className='ri-team-line'></i>
                          </button>

                          {/* Approve / Reject */}
                          {firm.status === 'pending' && (
                            <>
                              <button className='btn btn-sm btn-success' onClick={() => openModal(firm, 'approve')}>
                                <i className='ri-check-line me-1'></i> Approve
                              </button>
                              <button className='btn btn-sm btn-danger' onClick={() => openModal(firm, 'reject')}>
                                <i className='ri-close-line me-1'></i> Reject
                              </button>
                            </>
                          )}
                          {firm.status === 'rejected' && (
                            <button className='btn btn-sm btn-outline-success' onClick={() => openModal(firm, 'approve')}>
                              <i className='ri-refresh-line me-1'></i> Reconsider
                            </button>
                          )}
                          {firm.status === 'approved' && (
                            <button className='btn btn-sm btn-outline-danger' onClick={() => openModal(firm, 'reject')}>
                              <i className='ri-forbid-line me-1'></i> Revoke
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Create Firm Modal ─────────────────────────────────────────────── */}
      {createModalOpen && (
        <>
          <div className='modal show' style={{ display: 'block' }}>
            <div className='modal-dialog modal-lg'>
              <form className='modal-content' onSubmit={handleCreateFirm}>
                <div className='modal-header'>
                  <h5 className='modal-title'>Create New Firm</h5>
                  <button type='button' className='btn-close' onClick={() => setCreateModalOpen(false)}></button>
                </div>
                <div className='modal-body'>
                  <div className='row'>
                    <div className='col-md-6'>
                      {firmFieldRow('Firm Name', 'name', 'text', true, firmForm.name,
                        e => setFirmForm(f => ({ ...f, name: e.target.value })))}
                    </div>
                    {authorizedSignatoryFields(firmForm, setFirmForm, createAcknowledged, setCreateAcknowledged, 'create-authorized-signatory')}
                    <div className='col-md-8'>
                      {firmFieldRow('Address', 'address', 'text', false, firmForm.address,
                        e => setFirmForm(f => ({ ...f, address: e.target.value })))}
                    </div>
                    <div className='col-md-4'>
                      {firmFieldRow('City', 'city', 'text', false, firmForm.city,
                        e => setFirmForm(f => ({ ...f, city: e.target.value })))}
                    </div>
                    <div className='col-md-4'>
                      {stateField(firmForm.state, value => setFirmForm(f => ({ ...f, state: value })))}
                    </div>
                    <div className='col-md-4'>
                      {firmFieldRow('ZIP', 'zip', 'text', false, firmForm.zip,
                        e => setFirmForm(f => ({ ...f, zip: e.target.value })))}
                    </div>
                    <div className='col-md-4'>
                      {firmFieldRow('Phone', 'phone', 'text', false, firmForm.phone,
                        e => setFirmForm(f => ({ ...f, phone: e.target.value })))}
                    </div>
                    <div className='col-md-8'>
                      {firmFieldRow('Website', 'website', 'url', false, firmForm.website,
                        e => setFirmForm(f => ({ ...f, website: e.target.value })))}
                    </div>
                    <div className='col-md-6'>
                      <div className='mb-3'>
                        <label className='form-label'>Status</label>
                        <select
                          className='form-select'
                          value={firmForm.status}
                          onChange={e =>
                            setFirmForm(f => ({ ...f, status: e.target.value as any }))
                          }
                        >
                          <option value='approved'>Approved</option>
                          <option value='pending'>Pending</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-secondary' onClick={() => setCreateModalOpen(false)}>
                    Cancel
                  </button>
                  <button type='submit' className='btn btn-primary' disabled={firmFormSubmitting}>
                    {firmFormSubmitting ? 'Creating...' : 'Create Firm'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className='modal-backdrop fade show'></div>
        </>
      )}

      {/* ── Edit Firm Modal ───────────────────────────────────────────────── */}
      {editModalOpen && editFirm && (
        <>
          <div className='modal show' style={{ display: 'block' }}>
            <div className='modal-dialog modal-lg'>
              <form className='modal-content' onSubmit={handleEditFirm}>
                <div className='modal-header'>
                  <h5 className='modal-title'>Edit Firm — {editFirm.name}</h5>
                  <button type='button' className='btn-close' onClick={() => setEditModalOpen(false)}></button>
                </div>
                <div className='modal-body'>
                  <div className='row'>
                    <div className='col-md-6'>
                      {firmFieldRow('Firm Name', 'name', 'text', true, editForm.name,
                        e => setEditForm(f => ({ ...f, name: e.target.value })))}
                    </div>
                    {authorizedSignatoryFields(editForm, setEditForm, editAcknowledged, setEditAcknowledged, 'edit-authorized-signatory')}
                    <div className='col-md-8'>
                      {firmFieldRow('Address', 'address', 'text', false, editForm.address,
                        e => setEditForm(f => ({ ...f, address: e.target.value })))}
                    </div>
                    <div className='col-md-4'>
                      {firmFieldRow('City', 'city', 'text', false, editForm.city,
                        e => setEditForm(f => ({ ...f, city: e.target.value })))}
                    </div>
                    <div className='col-md-4'>
                      {stateField(editForm.state, value => setEditForm(f => ({ ...f, state: value })))}
                    </div>
                    <div className='col-md-4'>
                      {firmFieldRow('ZIP', 'zip', 'text', false, editForm.zip,
                        e => setEditForm(f => ({ ...f, zip: e.target.value })))}
                    </div>
                    <div className='col-md-4'>
                      {firmFieldRow('Phone', 'phone', 'text', false, editForm.phone,
                        e => setEditForm(f => ({ ...f, phone: e.target.value })))}
                    </div>
                    <div className='col-md-8'>
                      {firmFieldRow('Website', 'website', 'url', false, editForm.website,
                        e => setEditForm(f => ({ ...f, website: e.target.value })))}
                    </div>
                  </div>
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-secondary' onClick={() => setEditModalOpen(false)}>
                    Cancel
                  </button>
                  <button type='submit' className='btn btn-primary' disabled={editSubmitting}>
                    {editSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className='modal-backdrop fade show'></div>
        </>
      )}

      {/* ── Paralegal Management Modal ────────────────────────────────────── */}
      {memberModalOpen && memberFirm && (
        <>
          <div className='modal show' style={{ display: 'block' }}>
            <div className='modal-dialog modal-lg'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>
                    <i className='ri-team-line me-2'></i>Members — {memberFirm.name}
                  </h5>
                  <button type='button' className='btn-close' onClick={() => setMemberModalOpen(false)}></button>
                </div>
                <div className='modal-body'>
                  {memberLoading ? (
                    <div className='text-center py-3'>
                      <div className='spinner-border spinner-border-sm text-primary'></div>
                      <span className='ms-2'>Loading members…</span>
                    </div>
                  ) : members.length === 0 ? (
                    <p className='text-muted text-center py-3'>No members linked to this firm yet.</p>
                  ) : (
                    <table className='table table-sm mb-3'>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Designation</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map(member => (
                          <tr key={member.id}>
                            <td>{member.name}</td>
                            <td>{member.designation?.name || '—'}</td>
                            <td>{member.email}</td>
                            <td>
                              {member.phone || '—'}
                              {member.phone_extension ? ` x${member.phone_extension}` : ''}
                            </td>
                            <td className='text-end'>
                              <button type='button' className='btn btn-sm btn-outline-secondary me-1' onClick={() => startEditMember(member)}>
                                Edit
                              </button>
                              <button type='button' className='btn btn-sm btn-outline-warning' onClick={() => resetMemberPassword(member)}>
                                New password
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {!memberFormOpen ? (
                    <button
                      className='btn btn-outline-primary btn-sm'
                      onClick={() => {
                        setEditingMember(null);
                        setMemberForm({ ...EMPTY_MEMBER_FORM });
                        setMemberFormOpen(true);
                      }}
                    >
                      <i className='ri-user-add-line me-1'></i> Add Member
                    </button>
                  ) : (
                    <form onSubmit={handleSaveMember} className='border rounded p-3 bg-light mt-2'>
                      <h6 className='mb-3'>{editingMember ? 'Edit Member' : 'New Member'}</h6>
                      <div className='row g-2'>
                        <div className='col-md-6'>
                          <label className='form-label'>First Name <span className='text-danger'>*</span></label>
                          <input className='form-control form-control-sm' required value={memberForm.first_name}
                            onChange={e => setMemberForm(f => ({ ...f, first_name: e.target.value }))} />
                        </div>
                        <div className='col-md-6'>
                          <label className='form-label'>Last Name <span className='text-danger'>*</span></label>
                          <input className='form-control form-control-sm' required value={memberForm.last_name}
                            onChange={e => setMemberForm(f => ({ ...f, last_name: e.target.value }))} />
                        </div>
                        <div className='col-md-6'>
                          <label className='form-label'>Email <span className='text-danger'>*</span></label>
                          <input type='email' className='form-control form-control-sm' required value={memberForm.email}
                            onChange={e => setMemberForm(f => ({ ...f, email: e.target.value }))} />
                        </div>
                        <div className='col-md-3'>
                          <label className='form-label'>Phone</label>
                          <input className='form-control form-control-sm' value={memberForm.phone}
                            onChange={e => setMemberForm(f => ({ ...f, phone: e.target.value }))} />
                        </div>
                        <div className='col-md-3'>
                          <label className='form-label'>Extension</label>
                          <input className='form-control form-control-sm' value={memberForm.phone_extension}
                            onChange={e => setMemberForm(f => ({ ...f, phone_extension: e.target.value }))} />
                        </div>
                        <div className='col-md-6'>
                          <label className='form-label'>Designation <span className='text-danger'>*</span></label>
                          <select className='form-select form-select-sm' required value={memberForm.designation_id}
                            onChange={e => setMemberForm(f => ({ ...f, designation_id: e.target.value }))}>
                            <option value=''>Select</option>
                            {designations.map(designation => (
                              <option key={designation.id} value={designation.id}>{designation.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {!editingMember && (
                        <p className='text-muted small mt-2 mb-0'>A password is generated and emailed to this member.</p>
                      )}
                      <div className='d-flex gap-2 mt-3'>
                        <button type='submit' className='btn btn-primary btn-sm' disabled={memberSubmitting}>
                          {memberSubmitting ? 'Saving…' : editingMember ? 'Save Member' : 'Add Member'}
                        </button>
                        <button type='button' className='btn btn-secondary btn-sm' onClick={() => {
                          setMemberFormOpen(false);
                          setEditingMember(null);
                          setMemberForm({ ...EMPTY_MEMBER_FORM });
                        }}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
                <div className='modal-footer'>
                  <button className='btn btn-secondary' onClick={() => setMemberModalOpen(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show'></div>
        </>
      )}

      {/* ── Approve / Reject Modal ────────────────────────────────────────── */}
      {selectedFirm && (
        <div
          className={`modal ${actionModalOpen ? 'show' : ''}`}
          style={{ display: actionModalOpen ? 'block' : 'none' }}
        >
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>
                  {actionType === 'approve' ? 'Approve Firm' : 'Reject Firm'}
                </h5>
                <button type='button' className='btn-close' onClick={closeActionModal}></button>
              </div>
              <div className='modal-body'>
                <p>
                  {actionType === 'approve'
                    ? `Are you sure you want to approve "${selectedFirm.name}"?`
                    : `Are you sure you want to reject "${selectedFirm.name}"?`}
                </p>
                {actionType === 'reject' && (
                  <div className='form-group'>
                    <label htmlFor='rejectionReason' className='form-label'>
                      Reason for Rejection
                    </label>
                    <textarea
                      id='rejectionReason'
                      className='form-control'
                      rows={3}
                      value={rejectionReason}
                      onChange={e => setRejectionReason(e.target.value)}
                      placeholder='Please provide a reason for rejection'
                      required
                    ></textarea>
                  </div>
                )}
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-secondary' onClick={closeActionModal}>
                  Cancel
                </button>
                {actionType === 'approve' ? (
                  <button type='button' className='btn btn-success' onClick={handleApprove}>
                    Approve
                  </button>
                ) : (
                  <button
                    type='button'
                    className='btn btn-danger'
                    onClick={handleReject}
                    disabled={!rejectionReason.trim()}
                  >
                    Reject
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {actionModalOpen && <div className='modal-backdrop fade show'></div>}
    </div>
  );
};

export default AdminFirms;
